/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ForgotPassword, Step } from '@/components/content/ForgotPassword';
import { Login } from '@/components/content/Login';
import { Register } from '@/components/content/Register';
import { SessionDialogSx } from '@/components/content/SessionDialog/styles/DialogContent';
import { useSessionDialog } from '@/data/Content/SessionDialog';
import { useLocalization } from '@/data/Localization';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Dialog, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useForgotPasswordTitle } from '@/components/content/ForgotPassword/hooks/useForgotPasswordTitle';

export const SessionDialog: FC = () => {
	const { sessionDialogOpen, closeSessionDialog, openSessionDialog, selectedForm } =
		useSessionDialog();
	const labelsSignIn = useLocalization('SignIn');
	const labelsRegister = useLocalization('RegistrationLayout');

	const [forgotPasswordStep, setForgotPasswordStep] = useState<Step>(0);
	const forgotPasswordTitle = useForgotPasswordTitle({ step: forgotPasswordStep });

	const dialogTitle = (() => {
		if (selectedForm === 'signIn') return labelsSignIn.SignInTitle.t();
		if (selectedForm === 'forgot-password') return forgotPasswordTitle;
		if (selectedForm === 'register') return labelsRegister.Register.t();
	})();

	const onGoToLogin = useCallback(() => {
		setForgotPasswordStep(0);
		openSessionDialog('signIn');
	}, [setForgotPasswordStep, openSessionDialog]);

	const onGoToForgotPassword = useCallback(() => {
		openSessionDialog('forgot-password');
	}, [openSessionDialog]);

	return (
		<Dialog
			maxWidth={false}
			open={sessionDialogOpen}
			onClose={closeSessionDialog}
			PaperProps={{
				sx: SessionDialogSx.dialog,
			}}
		>
			<DialogTitle sx={SessionDialogSx.title}>
				<Typography variant="headingSegment" sx={SessionDialogSx.titleText}>
					{dialogTitle}
				</Typography>
				<HighlightOffIcon sx={SessionDialogSx.closeIcon} onClick={() => closeSessionDialog()} />
			</DialogTitle>
			<DialogContent sx={SessionDialogSx.content}>
				<Divider sx={SessionDialogSx.divider} />
				{selectedForm === 'signIn' ? (
					<Login
						id="login"
						isDialogView
						closeDrawer={closeSessionDialog}
						onGoToForgotPassword={onGoToForgotPassword}
					/>
				) : null}
				{selectedForm === 'register' ? <Register id="register" isDialogView /> : null}
				{selectedForm === 'forgot-password' ? (
					<ForgotPassword
						id="forgot-password"
						isSessionDialog
						onStepChange={setForgotPasswordStep}
						onGoToLogin={onGoToLogin}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
};
